// Copyright The Linux Foundation and each contributor to LFX.
// SPDX-License-Identifier: MIT

import { Component, ChangeDetectionStrategy, computed, input, output } from '@angular/core';
import { CommonModule } from '@angular/common';

// New comprehensive variant types based on requirements document
export type ButtonVariant =
  // New primary variants
  | 'primary' | 'secondary' | 'destructive' | 'tertiary' | 'neutral' | 'icon' | 'menu' | 'outline'
  // Legacy variants (for backward compatibility)
  | 'ghost-accent';

// Extended size types with new naming
export type ButtonSize =
  // New size naming
  | 'large' | 'small'
  // Legacy size (for backward compatibility)
  | 'default';

export type ButtonShape = 'rounded' | 'pill';
export type IconPosition = 'left' | 'right';

/**
 * LFX Button Component - Extended Coherence UI Kit Implementation
 *
 * A comprehensive button component supporting all Coherence UI Kit variants:
 *
 * **NEW STYLE VARIANTS** (7 total):
 * - primary: Main call-to-action buttons (blue theme)
 * - secondary: Secondary actions (light blue theme)
 * - destructive: Dangerous/irreversible actions (red theme)
 * - tertiary: Low-priority actions (transparent → blue theme)
 * - neutral: Neutral actions (transparent → gray theme)
 * - icon: Icon-only buttons (compact square format)
 * - menu: Menu navigation buttons with unique pill styling
 *
 * **LEGACY VARIANTS** (backward compatible):
 * - ghost-accent: Maps to tertiary
 *
 * **SIZE VARIANTS**:
 * - large: 40px height for prominent actions (was 'default')
 * - small: 32px height for primary actions, 28px for subtle actions
 * - default: Legacy alias for 'large' (backward compatibility)
 *
 * **SHAPE VARIANTS**:
 * - rounded: Standard border radius per variant
 * - pill: Fully rounded borders
 *
 * @example
 * ```html
 * <!-- New primary variants -->
 * <lfx-button variant="primary" size="large">Save Changes</lfx-button>
 * <lfx-button variant="secondary" size="small">Cancel</lfx-button>
 * <lfx-button variant="destructive">Delete Item</lfx-button>
 * <lfx-button variant="tertiary">Learn More</lfx-button>
 * <lfx-button variant="neutral">Skip</lfx-button>
 *
 * <!-- Menu navigation buttons with active state -->
 * <lfx-button variant="menu" [isActive]="true">Active Tab</lfx-button>
 * <lfx-button variant="menu" [isActive]="false">Inactive Tab</lfx-button>
 *
 * <!-- Icon-only buttons -->
 * <lfx-button variant="icon" size="large" icon="fas fa-plus" [iconOnly]="true" />
 * <lfx-button variant="icon" size="small" svgIcon="assets/img/svgs/settings.svg" [iconOnly]="true" />
 *
 * <!-- Legacy compatibility (still works) -->
 * <lfx-button variant="ghost-accent">Legacy Tertiary</lfx-button>
 * ```
 */
@Component({
  selector: 'lfx-button',
  imports: [CommonModule],
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <button
      [class]="buttonClasses()"
      [disabled]="isDisabled()"
      [type]="type()"
      (click)="handleClick($event)">

      @if (loading()) {
        <span class="lf-btn__loading-icon">
          <i class="fal fa-spinner-third"></i>
        </span>
      }

      @if (showLeftIcon()) {
        <span class="lf-btn__icon">
          @if (hasSvgIcon()) {
            <img [src]="svgIcon()" [alt]="buttonAriaLabel()" class="lf-btn__svg-icon">
          } @else {
            <i [class]="icon()"></i>
          }
        </span>
      }

      @if (showIconOnly()) {
        <span class="lf-btn__icon">
          @if (hasSvgIcon()) {
            <img [src]="svgIcon()" [alt]="buttonAriaLabel()" class="lf-btn__svg-icon">
          } @else {
            <i [class]="icon()"></i>
          }
        </span>
        <span class="lf-btn__text">{{ buttonAriaLabel() }}</span>
      } @else {
        <span class="lf-btn__text">
          <ng-content></ng-content>
        </span>
      }

      @if (showRightIcon()) {
        <span class="lf-btn__icon">
          @if (hasSvgIcon()) {
            <img [src]="svgIcon()" [alt]="buttonAriaLabel()" class="lf-btn__svg-icon">
          } @else {
            <i [class]="icon()"></i>
          }
        </span>
      }
    </button>
  `,
  styles: [`
    :host {
      display: inline-block;
    }

    .lf-btn__svg-icon {
      width: inherit;
      height: inherit;
      object-fit: contain;
      pointer-events: none;
    }
  `]
})
export class ButtonComponent {
  // Input properties (readonly as per dev guide)
  readonly variant = input<ButtonVariant>('primary');
  readonly size = input<ButtonSize>('default');
  readonly shape = input<ButtonShape>('rounded');
  readonly type = input<'button' | 'submit' | 'reset'>('button');
  readonly disabled = input<boolean>(false);
  readonly loading = input<boolean>(false);
  readonly icon = input<string>(''); // FontAwesome icon class (e.g., "fas fa-plus")
  readonly svgIcon = input<string>(''); // SVG asset path (e.g., "assets/img/svgs/csv.svg")
  readonly iconPosition = input<IconPosition>('left');
  readonly iconOnly = input<boolean>(false);
  readonly ariaLabel = input<string>('');

  // Menu variant specific - active state for navigation buttons
  readonly isActive = input<boolean>(false);

  // Output events
  readonly clicked = output<MouseEvent>();

  // Protected computed properties for template (as per dev guide)
  protected readonly buttonClasses = computed(() => {
    const classes = [
      'lf-btn',
      `lf-btn--${this.resolvedVariant()}`,
      `lf-btn--${this.resolvedSize()}`,
      `lf-btn--${this.shape()}`
    ];

    if (this.loading()) {
      classes.push('lf-btn--loading');
    }

    if (this.iconOnly()) {
      classes.push('lf-btn--icon-only');
    }

    // Add active class for menu variant when isActive is true
    if (this.isActive() && this.resolvedVariant() === 'menu') {
      classes.push('active');
    }

    return classes.join(' ');
  });

  // Backward compatibility mapping for variants
  protected readonly resolvedVariant = computed(() => {
    const variant = this.variant();

    // Legacy variant mapping to new variants
    switch (variant) {
      case 'ghost-accent':
        return 'tertiary';   // ghost-accent → tertiary
      default:
        return variant;      // primary, secondary, destructive, tertiary, neutral, outline, icon, menu
    }
  });

  // Backward compatibility mapping for sizes
  protected readonly resolvedSize = computed(() => {
    const size = this.size();

    // Legacy size mapping
    switch (size) {
      case 'default':
        return 'large';      // default → large (backward compatibility)
      default:
        return size;         // large, small
    }
  });

  protected readonly isDisabled = computed(() =>
    this.disabled() || this.loading()
  );

  protected readonly hasAnyIcon = computed(() =>
    Boolean(this.icon() || this.svgIcon())
  );

  protected readonly hasSvgIcon = computed(() =>
    Boolean(this.svgIcon())
  );

  protected readonly showLeftIcon = computed(() =>
    this.hasAnyIcon() && this.iconPosition() === 'left' && !this.iconOnly() && !this.loading()
  );

  protected readonly showRightIcon = computed(() =>
    this.hasAnyIcon() && this.iconPosition() === 'right' && !this.iconOnly() && !this.loading()
  );

  protected readonly showIconOnly = computed(() =>
    this.iconOnly() && this.hasAnyIcon() && !this.loading()
  );

  protected readonly buttonAriaLabel = computed(() =>
    this.ariaLabel() || 'Button'
  );

  // Protected event handler (named for what it DOES, as per dev guide)
  protected handleClick(event: MouseEvent): void {
    if (!this.isDisabled()) {
      this.clicked.emit(event);
    }
  }
}
